import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-curtron-ink tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-curtron-sub mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function Card({ className, children, ...rest }) {
  return (
    <div className={cn("bg-curtron-surface border border-curtron-line rounded-md", className)} {...rest}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "green", testid }) {
  const accents = {
    green: "text-curtron-green bg-curtron-green/10",
    ochre: "text-curtron-ochre bg-curtron-ochre/15",
    terracotta: "text-curtron-terracotta bg-curtron-terracotta/12",
    info: "text-curtron-info bg-curtron-info/12",
    moss: "text-curtron-moss bg-curtron-moss/18",
  };
  return (
    <Card className="p-4 sm:p-5 hover:shadow-sm transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.05em] font-medium text-curtron-muted">{label}</div>
          <div className="font-heading text-2xl sm:text-3xl font-bold text-curtron-ink mt-1.5">{value}</div>
        </div>
        {Icon && (
          <div className={cn("w-9 h-9 rounded-md flex items-center justify-center", accents[accent])}>
            <Icon className="w-[18px] h-[18px]" />
          </div>
        )}
      </div>
    </Card>
  );
}

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-4">
      {Icon && <div className="w-12 h-12 rounded-full bg-curtron-line/60 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-curtron-muted" /></div>}
      <div className="font-heading font-semibold text-curtron-ink">{title}</div>
      {subtitle && <div className="text-sm text-curtron-sub mt-1 max-w-sm">{subtitle}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loading({ className }) {
  return (
    <div className={cn("flex items-center justify-center py-20", className)}>
      <Loader2 className="w-6 h-6 animate-spin text-curtron-green" />
    </div>
  );
}

export function Btn({ variant = "primary", className, as, ...rest }) {
  const styles = {
    primary: "bg-curtron-green text-white hover:bg-curtron-greenHover",
    secondary: "bg-curtron-surface text-curtron-ink border border-curtron-line hover:bg-curtron-bg",
    destructive: "bg-curtron-terracotta text-white hover:bg-[#A35549]",
    ghost: "text-curtron-sub hover:bg-black/5",
  };
  const Comp = as || "button";
  return (
    <Comp
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-60 h-9",
        styles[variant],
        className
      )}
      {...rest}
    />
  );
}
